import React, { type FC, useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	CloseButton,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ChoreographerAssetsContextProvider } from '@confluence/choreographer-services/entry-points/ChoreographerAssetsContext.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../controller/Controller.tsx';

export type CloseConfirmationDialogProps = {
	onDiscard: () => void;
	onCancel: () => void;
	focusRef?: React.RefObject<HTMLElement>;
};

const i18n = defineMessages({
	heading: {
		id: 'share-and-restrict-dialog.close-confirmation.heading',
		defaultMessage: 'Unsaved changes',
		description: 'Heading for a pop-up modal that asks if a user wants to discard staged changes',
	},
	body: {
		id: 'share-and-restrict-dialog.close-confirmation.body',
		defaultMessage:
			'You have unsaved changes that will be lost if you close the share window. Do you want to discard them?',
		description:
			'Body for a pop-up modal for the (share-and-restrict-dialog.header.title.share) dialog',
	},
	cancelButton: {
		id: 'share-and-restrict-dialog.close-confirmation.cancel',
		defaultMessage: 'Cancel',
		description: 'The label for a button that cancels the process of closing a dialog',
	},
	discardButton: {
		id: 'share-and-restrict-dialog.close-confirmation.discard',
		defaultMessage: 'Discard',
		description: 'The label for a button for confirming that unsaved changes can be discarded',
	},
});

const CloseConfirmationDialogContent: FC<CloseConfirmationDialogProps> = ({
	onDiscard,
	onCancel,
	focusRef,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { Modal } = useChoreographerAssets();

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'discardShareAndRestrictChanges',
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
			},
		}).fire();
		onDiscard();
	}, [createAnalyticsEvent, onDiscard]);

	return (
		<Modal
			width="small"
			shouldReturnFocus={focusRef}
			testId="modal-dialog-share-close-confirmation"
		>
			<ModalHeader>
				<ModalTitle>
					<FormattedMessage {...i18n.heading} />
				</ModalTitle>
				<CloseButton onClick={onCancel} />
			</ModalHeader>
			<ModalBody>
				<FormattedMessage {...i18n.body} />
			</ModalBody>
			<ModalFooter>
				<Button onClick={onCancel}>
					<FormattedMessage {...i18n.cancelButton} />
				</Button>
				<Button appearance="warning" onClick={handleClick}>
					<FormattedMessage {...i18n.discardButton} />
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export const CloseConfirmationDialog: FC<CloseConfirmationDialogProps> = (props) => {
	return (
		<ChoreographerAssetsContextProvider>
			<CloseConfirmationDialogContent {...props} />
		</ChoreographerAssetsContextProvider>
	);
};
